import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AxiosInstance } from 'axios';
import { AxiosWrapper } from 'services/AxiosWrapper';
import { BookingManagerApiService } from 'services/BookingManagerApi';
import { ItineraryManagerApiService } from 'services/ItineraryManagerApi';
import { useNotifications } from 'hooks/useNotifications';

export const useVouchersV2BookingManagerQueries = (
  bookingManagerApi: BookingManagerApiService<AxiosWrapper<AxiosInstance>>,
  itineraryManagerApi: ItineraryManagerApiService<AxiosWrapper<AxiosInstance>>,
  bookingUuid: string | undefined
) => {
  const queryClient = useQueryClient();
  const notificationOps = useNotifications();

  return {
    invalidateGetVouchersV2Instance: () =>
      queryClient.invalidateQueries({ queryKey: ['getVouchersV2Overview', 'getVouchersV2View', bookingUuid] }),
    postGenerateVouchersV2Instance: useMutation({
      mutationFn: async (bookingUuid: string) => {
        return bookingManagerApi.postGenerateVouchersV2Instance(bookingUuid);
      },
      onSuccess: () => {
        notificationOps.showSuccessNotification('Voucher generated successfully');

        // Reset queries to clear any error state (like 404s)
        queryClient.resetQueries({ queryKey: ['getVouchersV2Overview', bookingUuid] });
        queryClient.resetQueries({ queryKey: ['getVouchersV2View', bookingUuid] });

        // Then immediately refetch them
        queryClient.refetchQueries({ queryKey: ['getVouchersV2Overview', bookingUuid] });
        queryClient.refetchQueries({ queryKey: ['getVouchersV2View', bookingUuid] });

        // Open the voucher page in a new tab
        window.open(`/voucher/${bookingUuid}`, '_blank');
      },
      onError: () => {
        notificationOps.showErrorNotification('Failed to generate itinerary');
      },
    }),
    getVouchersV2Overview: useQuery({
      queryKey: ['getVouchersV2Overview', bookingUuid],
      queryFn: async () => {
        if (!bookingUuid) {
          return;
        }
        return itineraryManagerApi.getVouchersV2Overview(bookingUuid);
      },
    }),
  };
};
